import { Controller } from '@hotwired/stimulus'
import { patch, post } from '@rails/request.js'

// Runs a walkthrough: the <template> steps Components::Walkthrough renders, shown one at a
// time in a single card, with the element the step talks about lit up behind it.
//
// A step advances on Next, or — where it asks the user to do something — on a
// named DOM event, so the walkthrough watches the thing actually happen rather than
// taking a click as proof. Nothing here blocks the page: the spotlight is
// pointer-events-none throughout, which is what lets a step hand the page back
// without the walkthrough losing its place.
// Every anchored step looks the same at first: the page dimmed, the anchor cut
// out and ringed, the card beside it. Reaching for the anchor lifts the dimming
// and moves the card to a corner, so the user works an undimmed page while the
// step waits.
//
// The card is a dialog but not a modal one: focus moves to it, or to its Next
// button, on each step and is never trapped there, because the steps that wait
// on the user need them working the page behind it.
//
// Every transition is recorded, in order: the walkthrough is started when it
// appears, then each step as it is shown, and how it ended.
//
// A step is passed over when there is nothing left for it to do: what it waits
// to see is already on the page, or what it points at is not. That is decided
// when the step is reached, so what the user did during earlier steps counts.
//
// A step template reads (see config/walkthroughs.yml):
//   data-key        names the step; recorded as the user reaches it
//   data-title      the card heading; the template's content is the body
//   data-anchor     what to point at; the card centres on screen without one
//   data-await      event that advances the step in place of Next
//   data-have       selector for what the step waits to see on the page
//   data-count      how many of those; the step is passed over once the page has them
//   data-desktop    drop the step below the lg breakpoint
//   data-placement  above, below, left or right of the anchor; default picks
//                   below if the card fits there, else above
const DESKTOP_WIDTH = 1024
const SPOTLIGHT_PADDING = 8
const CARD_GAP = 16
const DIM = '0 0 0 9999px rgba(15, 23, 42, 0.55)'
// Reaching for the anchor, by pointer or by key, is what puts the spotlight away.
const INTERACTIONS = ['pointerdown', 'keydown']

export default class extends Controller {
  static targets = ['step', 'card', 'title', 'body', 'spotlight', 'counter', 'next', 'done', 'end']
  static values = { url: String, feature: String }

  connect () {
    this.index = 0
    this.steps = this.stepTargets.filter((step) => this.applies(step))
    this.reposition = () => this.place()
    window.addEventListener('resize', this.reposition)
    window.addEventListener('scroll', this.reposition, true) // capture: panels scroll, not the page
    this.start()
    this.show()
  }

  disconnect () {
    window.removeEventListener('resize', this.reposition)
    window.removeEventListener('scroll', this.reposition, true)
    this.stopWaiting()
    this.unwatchAnchor()
  }

  // Being shown is the start. Every later request is chained on this one, so
  // they carry the row's id and land in the order they were made.
  start () {
    this.requests = post(this.urlValue, { body: { walkthrough: { feature: this.featureValue } }, responseKind: 'json' })
      .then((response) => response.json)
      .then(({ id }) => { this.walkthroughUrl = `${this.urlValue}/${id}` })
  }

  next () {
    this.index += 1
    this.show()
  }

  finish () {
    this.record({ outcome: 'finished' })
    this.close()
  }

  // Skip, and Esc from the window, both land here: leaving early is skipping.
  // On the last step there is nothing left to leave, so Esc finishes instead.
  skip () {
    if (this.index === this.steps.length - 1) return this.finish()

    this.record({ outcome: 'skipped' })
    this.close()
  }

  record (transition) {
    const step = this.current?.dataset.key
    this.requests = this.requests.then(() => patch(this.walkthroughUrl, { body: { walkthrough: { step, ...transition } } }))
  }

  // Closing settles the walkthrough, so the page's restart button — the way to
  // run it again — is revealed on the way out.
  close () {
    this.stopWaiting()
    this.unwatchAnchor()
    document.querySelector('[data-walkthrough-restart]')?.classList.remove('hidden')
    this.element.remove()
  }

  show () {
    this.stopWaiting()
    while (this.steps[this.index] && !this.needed(this.steps[this.index])) this.index += 1
    const step = this.steps[this.index]
    if (!step) return this.finish()

    this.current = step
    this.position = (this.position || 0) + 1
    const last = this.index === this.steps.length - 1
    const awaiting = Boolean(step.dataset.await)

    this.cardTarget.dataset.walkthroughStep = step.dataset.key
    this.titleTarget.textContent = step.dataset.title
    this.bodyTarget.replaceChildren(step.content.cloneNode(true))
    this.counterTarget.textContent = `${this.position} / ${this.position + this.steps.length - this.index - 1}`
    this.nextTarget.classList.toggle('hidden', awaiting || last)
    this.doneTarget.classList.toggle('hidden', awaiting || !last)
    this.endTarget.classList.toggle('hidden', last) // Done is the only way off the last step
    this.cardTarget.classList.remove('hidden')

    this.anchorFor(step)?.scrollIntoView({ block: 'center' })
    this.anchorReached = false
    this.spotlightTarget.classList.remove('hidden')
    this.place()
    this.watchAnchor(step)
    this.record({})

    if (awaiting) {
      this.waitFor(step)
      this.cardTarget.focus() // the page is theirs now; the card still announces itself
    } else {
      (last ? this.doneTarget : this.nextTarget).focus()
    }
  }

  waitFor (step) {
    const name = step.dataset.await
    // The event can fire before what the step waits to see has all arrived — one
    // panel added where the step needs two — so the page is checked, not the event.
    const listener = () => {
      if (step.dataset.have && !this.satisfied(step)) return

      this.next()
    }

    this.waiting = { name, listener }
    document.addEventListener(name, listener)
  }

  stopWaiting () {
    if (!this.waiting) return

    document.removeEventListener(this.waiting.name, this.waiting.listener)
    this.waiting = null
  }

  // The spotlight is a pointer, not a cage: once the user reaches for what it
  // points at it has done its job, and gets out of their way for the rest of
  // the step. Listened for on the document, in the capture phase, so an anchor
  // a stream re-renders mid-step still counts and nothing it does with the
  // event can hide it from us.
  watchAnchor (step) {
    this.unwatchAnchor()
    const selector = step.dataset.anchor
    if (!selector) return

    this.reached = (event) => {
      if (event.target.closest?.(selector)) this.stepAside()
    }
    INTERACTIONS.forEach((name) => document.addEventListener(name, this.reached, true))
  }

  unwatchAnchor () {
    if (!this.reached) return

    INTERACTIONS.forEach((name) => document.removeEventListener(name, this.reached, true))
    this.reached = null
  }

  stepAside () {
    this.anchorReached = true
    this.spotlightTarget.classList.add('hidden')
    this.place()
  }

  place () {
    const step = this.steps[this.index]
    if (!step) return

    const rect = this.rectFor(step)

    this.lightUp(rect)
    if (this.anchorReached) this.toCorner()
    else if (rect) this.beside(rect, step.dataset.placement)
    else this.toMiddle()
  }

  // The dimming is the spotlight's own shadow, spread wide enough to cover the
  // viewport, so a step with nothing to point at shrinks it to a point rather
  // than hiding it and taking the dimming with it. The ring is an outline, not
  // Tailwind's ring, because a ring is a box-shadow too and the dimming would
  // paint over it.
  lightUp (rect) {
    this.spotlightTarget.classList.toggle('outline-2', Boolean(rect))
    this.spotlightTarget.style.boxShadow = DIM
    Object.assign(this.spotlightTarget.style, rect
      ? {
          top: `${rect.top - SPOTLIGHT_PADDING}px`,
          left: `${rect.left - SPOTLIGHT_PADDING}px`,
          width: `${rect.width + SPOTLIGHT_PADDING * 2}px`,
          height: `${rect.height + SPOTLIGHT_PADDING * 2}px`
        }
      : { top: '50%', left: '50%', width: '1px', height: '1px' })
  }

  beside (rect, placement = this.roomiest(rect)) {
    const card = this.cardTarget
    const centredTop = rect.top + rect.height / 2 - card.offsetHeight / 2
    const centredLeft = rect.left + rect.width / 2 - card.offsetWidth / 2
    const positions = {
      above: [rect.top - card.offsetHeight - CARD_GAP, centredLeft],
      below: [rect.bottom + CARD_GAP, centredLeft],
      left: [centredTop, rect.left - card.offsetWidth - CARD_GAP],
      right: [centredTop, rect.right + CARD_GAP]
    }
    const [top, left] = positions[placement] || positions.below

    Object.assign(card.style, {
      top: `${this.onScreen(top, card.offsetHeight, window.innerHeight)}px`,
      left: `${this.onScreen(left, card.offsetWidth, window.innerWidth)}px`
    })
  }

  roomiest (rect) {
    const fitsBelow = rect.bottom + CARD_GAP + this.cardTarget.offsetHeight + CARD_GAP <= window.innerHeight

    return fitsBelow ? 'below' : 'above'
  }

  toMiddle () {
    const card = this.cardTarget
    Object.assign(card.style, {
      top: `${(window.innerHeight - card.offsetHeight) / 2}px`,
      left: `${(window.innerWidth - card.offsetWidth) / 2}px`
    })
  }

  // Out of the way once the user has reached for the anchor. Bottom left: a
  // chooser slides in from the right, and a panel is dragged and resized from
  // its own edges, so nothing a step needs lives down here.
  toCorner () {
    const card = this.cardTarget
    Object.assign(card.style, {
      top: `${window.innerHeight - card.offsetHeight - CARD_GAP}px`,
      left: `${CARD_GAP}px`
    })
  }

  // Keeps the card in view whichever edge its anchor sits against.
  onScreen (position, size, extent) {
    return Math.min(Math.max(CARD_GAP, position), Math.max(CARD_GAP, extent - size - CARD_GAP))
  }

  rectFor (step) {
    const rect = this.anchorFor(step)?.getBoundingClientRect()

    return rect && rect.width > 0 ? rect : null
  }

  anchorFor (step) {
    return step.dataset.anchor ? document.querySelector(step.dataset.anchor) : null
  }

  applies (step) {
    return step.dataset.desktop !== 'true' || window.innerWidth >= DESKTOP_WIDTH
  }

  // A step is passed over when there is nothing left for it to do: what it waits
  // to see is already on the page, or what it points at is not.
  needed (step) {
    if (step.dataset.anchor && !document.querySelector(step.dataset.anchor)) return false

    return !this.satisfied(step)
  }

  satisfied (step) {
    if (!step.dataset.have) return false

    return document.querySelectorAll(step.dataset.have).length >= Number(step.dataset.count || 1)
  }
}
